// Values for first tab links
export const firstTab = [
  {
    name: 'Benchmarking',
    value: 'benchmarks',
    active: false,
  },
  {
    name: 'Reports',
    value: 'emissions',
    active: true,
  },
];

// Values for second tab links
export const secondTab = [
  {
    name: 'Three years',
    value: 'threeYears',
    active: false,
  },
  {
    name: 'Yearly',
    value: 'yearly',
    active: false,
  },
  {
    name: 'Monthly',
    value: 'monthly',
    active: false,
  },
];

// Options FOR LINE CHART
export const lineChartOptions = {
  // datasetFill: true,
  responsive: true,
  plugins: {
    legend: {
      position: 'top' as const,
    },
    title: {
      display: false,
      text: 'Line Chart',
    },
    decimation: {
      enabled: true,
      threshold: 20,
    },
  },
  maintainAspectRatio: false,
  scales: {
    x: {
      ticks: {
        // maxTicksLimit: 20,
      },
    },
  },
};

// Split a long activity label into a few shorter lines (on word boundaries) so
// slanted x-axis labels stay compact instead of one very long string. Shared by
// the overall stacked chart and the single-location bar chart.
const wrapLabel = (label: string, maxLen = 20): string[] => {
  const words = String(label).split(' ');
  const lines: string[] = [];
  let current = '';
  words.forEach((word) => {
    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length > maxLen && current) {
      lines.push(current);
      current = word;
    } else {
      current = candidate;
    }
  });
  if (current) lines.push(current);
  return lines;
};

// Options for Bar Chart (single-location / non-stacked activity view). Mirrors
// the overall stacked chart: slanted, multi-line x-axis labels.
export const barChartOptions = {
  responsive: true,
  plugins: {
    legend: {
      position: 'top' as const,
    },
    title: {
      display: false,
      text: 'Chart.js Bar Chart',
    },
  },
  scales: {
    x: {
      grid: { display: false, drawBorder: false },
      ticks: {
        color: '#6b7478',
        font: { size: 10.5 },
        // Slant the labels at 45° so long activity names never overlap their
        // neighbours, at any browser zoom, instead of colliding horizontally,
        // and split long names onto several lines so they stay compact.
        maxRotation: 45,
        minRotation: 45,
        autoSkip: false,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        callback(this: any, value: any) {
          return wrapLabel(this.getLabelForValue(value));
        },
      },
    },
    y: {
      beginAtZero: true,
      grid: { color: '#eef2f2', drawBorder: false },
      ticks: { color: '#6b7478', font: { size: 11.5 } },
    },
  },
  maintainAspectRatio: false,
};

// Activity chart for "Overall Total": one stacked segment per location. The
// axes and gridlines are hidden and each bar's total is drawn above it, so the
// chart reads as the flat bar-and-label design rather than a plotted grid.
const stackTotalLabels = {
  id: 'stackTotalLabels',
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  afterDatasetsDraw(chart: any) {
    const { ctx, scales } = chart;
    if (!scales?.y) return;
    const totals: { [index: number]: number } = {};
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    chart.data.datasets.forEach((dataset: any, datasetIndex: number) => {
      if (chart.getDatasetMeta(datasetIndex).hidden) return;
      dataset.data.forEach((value: number, index: number) => {
        totals[index] = (totals[index] || 0) + (Number(value) || 0);
      });
    });
    ctx.save();
    ctx.font = '11.5px Segoe UI, system-ui, sans-serif';
    ctx.fillStyle = '#6b7478';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    // Draw left-to-right and skip any label that would overlap the previous
    // one, so on small screens with many activities the numbers don't collide
    // and "mix up" — wide screens still show every label.
    let lastRight = -Infinity;
    const gap = 4;
    Object.keys(totals)
      .map(Number)
      .sort((a, b) => a - b)
      .forEach((index) => {
        const total = totals[index];
        if (!total) return;
        const bar = chart.getDatasetMeta(0)?.data?.[index];
        if (!bar) return;
        const text = total.toLocaleString('en-AU', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
        const halfWidth = ctx.measureText(text).width / 2;
        if (bar.x - halfWidth < lastRight + gap) return;
        ctx.fillText(text, bar.x, scales.y.getPixelForValue(total) - 6);
        lastRight = bar.x + halfWidth;
      });
    ctx.restore();
  },
};

export const stackedBarChartPlugins = [stackTotalLabels];

export const stackedBarChartOptions = {
  responsive: true,
  // Extra top padding leaves room for the per-bar total labels drawn above
  // each column, so they don't collide with the legend.
  layout: { padding: { top: 28 } },
  plugins: {
    legend: {
      position: 'top' as const,
      align: 'center' as const,
      labels: {
        boxWidth: 10,
        boxHeight: 10,
        padding: 14,
        color: '#6b7478',
        font: { size: 12 },
        usePointStyle: false,
      },
    },
    title: {
      display: false,
      text: 'Chart.js Stacked Bar Chart',
    },
    tooltip: {
      // On hover show only the location (the dataset label) and its value —
      // not the activity name, which is already the x-axis label.
      callbacks: {
        title: () => '',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        label: (context: any) => `${context.dataset.label}: ${Number(context.parsed.y).toFixed(2)} t`,
      },
    },
  },
  scales: {
    x: {
      stacked: true,
      grid: { display: false, drawBorder: false },
      ticks: {
        color: '#6b7478',
        font: { size: 10.5 },
        // Slant the labels at 45° so long activity names never overlap their
        // neighbours, at any browser zoom, instead of colliding horizontally,
        // and split long names onto several lines so they stay compact.
        maxRotation: 45,
        minRotation: 45,
        autoSkip: false,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        callback(this: any, value: any) {
          return wrapLabel(this.getLabelForValue(value));
        },
      },
    },
    y: {
      stacked: true,
      display: false,
      grid: { display: false, drawBorder: false },
      beginAtZero: true,
      // Headroom above the tallest bar so its total label sits clear of the
      // legend instead of overlapping it.
      grace: '16%',
    },
  },
  maintainAspectRatio: false,
};
